import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import React from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { IUnderlyingPolicy } from "../../../../../models/user/policyDetails/PolicyDetails";
import CustomIcon from "../../../../../common/CustomIcon/CustomIcon";
import RelatedPolicyStyles from "./RelatedPolicyStyles";
import { currencyFormat } from "../../../../../utils/Utility";
import { useTranslation } from "react-i18next";

interface IProps {
  policy: IUnderlyingPolicy;
  index: number;
}

const RelatedPolicy: React.FC<IProps> = ({ policy, index }) => {
  const { t } = useTranslation();

  const getIcon = (lobCode: string) => {
    switch (lobCode) {
      case "Automobile":
        return <CustomIcon iconName="Car" />;
      case "Recreational Vehicles":
        return <CustomIcon iconName="Car" />;
      case "Residences":
        return <CustomIcon iconName="Home" />;
      case "PU":
        return <CustomIcon iconName="Umbrella" />;
    }
  };

  function hasDollarInValue(amount: string) {
    return amount.includes("$");
  }

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={
          <ArrowDropDownIcon
            sx={{ border: "1px solid #cccccc", borderRadius: "50px" }}
          />
        }
      >
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gap: "20px",
            alignItems: "center",
            gridTemplateColumns: "4% repeat(3, 32%)",
            "@media(max-width:767px)": {
              alignItems: "start",
              gridTemplateColumns: "repeat(2, 1fr)",
              gridTemplateRows: "auto auto auto",
            },
            "@media (min-width:768px) and (max-width:1023px)": {
              gridTemplateColumns: "repeat(4, 1fr)",
            },
          }}
        >
          <Box>{getIcon(policy.underlyingPolicyTypeCd)}</Box>
          <Box>
            <Typography sx={RelatedPolicyStyles.heading}>
              {policy.underlyingPolicyTypeCd === "Automobile" ||
              policy.underlyingPolicyTypeCd === "Recreational Vehicles"
                ? t("Vehicle Count")
                : policy.underlyingPolicyTypeCd === "Residences"
                ? t("Address")
                : ""}
            </Typography>
            <Typography
              sx={{
                ...RelatedPolicyStyles.content,
                "@media (min-width:600px) and (max-width:1023px)": {
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                },
              }}
            >
              {policy.underlyingPolicyTypeCd === "Automobile" ||
              policy.underlyingPolicyTypeCd === "Recreational Vehicles"
                ? policy.underlyingPolicyRiskList.length
                : policy.underlyingPolicyTypeCd === "Residences"
                ? `${policy.underlyingPolicyRiskList[0].address.addr1} ${policy.underlyingPolicyRiskList[0].address.city} ${policy.underlyingPolicyRiskList[0].address.county} ${policy.underlyingPolicyRiskList[0].address.postalCode} ${policy.underlyingPolicyRiskList[0].address.regionISOCd} ${policy.underlyingPolicyRiskList[0].address.stateProvCd}`
                : ""}
            </Typography>
          </Box>
          <Box>
            <Typography sx={RelatedPolicyStyles.heading}>
              {t("Policy Number")}
            </Typography>
            <Typography sx={RelatedPolicyStyles.content}>
              {policy.underlyingPolicyNumber}
            </Typography>
          </Box>
          <Box>
            <Typography sx={RelatedPolicyStyles.heading}>
              {t("Company")}
            </Typography>
            <Typography sx={RelatedPolicyStyles.content}>
              {policy.underlyingCarrierName}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: "grid",
            border: "0.5px solid #cccccc",
            padding: "20px",
            borderRadius: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid lightslategrey",
              "@media (max-width:762px)": {
                display: "grid",
                gridTemplateColumns: "1fr 0.5fr",
              },
            }}
          >
            <Typography sx={RelatedPolicyStyles.heading}>
              {t("Coverages")}
            </Typography>
            <Typography sx={RelatedPolicyStyles.content}>
              {t("Limit")}
            </Typography>
          </Box>
          {policy.underlyingBodilyInjuryPerPersonLimit && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #cccccc",
                padding: "10px 0px",
                "@media (max-width:762px)": {
                  display: "grid",
                  gridTemplateColumns: "1fr 0.5fr",
                },
              }}
            >
              <Typography sx={RelatedPolicyStyles.heading}>
                {t("Bodily Injury Per Person")}
              </Typography>
              <Typography sx={RelatedPolicyStyles.content}>
                {policy.underlyingBodilyInjuryPerPersonLimit
                  ? hasDollarInValue(
                      policy.underlyingBodilyInjuryPerPersonLimit
                    )
                    ? policy.underlyingBodilyInjuryPerPersonLimit
                    : currencyFormat(
                        "us",
                        +policy.underlyingBodilyInjuryPerPersonLimit
                      )
                  : "-"}
              </Typography>
            </Box>
          )}
          {policy.underlyingBodilyInjuryOccurrenceLimit && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 0px",
                "@media (max-width:762px)": {
                  display: "grid",
                  gridTemplateColumns: "1fr 0.5fr",
                },
              }}
            >
              <Typography sx={RelatedPolicyStyles.heading}>
                {t("Bodily Injury Per Accident")}
              </Typography>
              <Typography sx={RelatedPolicyStyles.content}>
                {policy.underlyingBodilyInjuryOccurrenceLimit
                  ? hasDollarInValue(
                      policy.underlyingBodilyInjuryOccurrenceLimit
                    )
                    ? policy.underlyingBodilyInjuryOccurrenceLimit
                    : currencyFormat(
                        "us",
                        +policy.underlyingBodilyInjuryOccurrenceLimit
                      )
                  : "-"}
              </Typography>
            </Box>
          )}
          {policy.underlyingPropertyDamagedOccurrenceLimit && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: policy.underlyingBodilyInjuryPerPersonLimit
                  ? "1px solid #cccccc"
                  : "none",
                padding: "10px 0px",
                "@media (max-width:762px)": {
                  display: "grid",
                  gridTemplateColumns: "1fr 0.5fr",
                },
              }}
            >
              <Typography sx={RelatedPolicyStyles.heading}>
                {policy.underlyingPolicyTypeCd === "Automobile" ||
                policy.underlyingPolicyTypeCd === "Recreational Vehicles"
                  ? t("Property Damage Per Occurance")
                  : t("Personal Liability Per Occurance")}
              </Typography>
              <Typography sx={RelatedPolicyStyles.content}>
                {policy.underlyingPropertyDamagedOccurrenceLimit
                  ? hasDollarInValue(
                      policy.underlyingPropertyDamagedOccurrenceLimit
                    )
                    ? policy.underlyingPropertyDamagedOccurrenceLimit
                    : currencyFormat(
                        "us",
                        +policy.underlyingPropertyDamagedOccurrenceLimit
                      )
                  : "-"}
              </Typography>
            </Box>
          )}
        </Box>
        {(policy.underlyingPolicyTypeCd === "Automobile" ||
          policy.underlyingPolicyTypeCd === "Recreational Vehicles") && (
          <Box sx={{ marginTop: "20px" }}>
            <Typography
              sx={{
                maxWidth: "257px",
                fontFamily: "Playfair Display",
                fontSize: "20px",
                fontWeight: "400",
                color: "rgba(56, 61, 78, 1)",
                marginBottom: "10px",
              }}
            >
              {t("Vehicle Listing")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: "15px",
              }}
            >
              {policy.underlyingPolicyRiskList.map((risk) => (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,33%)",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid #cccccc",
                    padding: "10px 20px",
                    borderRadius: "10px",
                    gap: "15px",
                    "@media(max-width:767px)": {
                      alignItems: "start",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      overflowX: "scroll",
                    },
                  }}
                >
                  <Box>
                    <Typography sx={RelatedPolicyStyles.heading}>
                      {t("Year")}
                    </Typography>
                    <Typography sx={RelatedPolicyStyles.content}>
                      {risk.year}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={RelatedPolicyStyles.heading}>
                      {t("Make & Model")}
                    </Typography>
                    <Typography sx={RelatedPolicyStyles.content}>
                      {risk.make}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={RelatedPolicyStyles.heading}>
                      {t("Type")}
                    </Typography>
                    <Typography sx={RelatedPolicyStyles.content}>
                      {risk.exposureTypeCd}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default RelatedPolicy;
